/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import debounce from 'lodash.debounce';
import {translate} from '@ion/core/src/locale/locale';
import Hidden from '@ion/dls/src/layout/Hidden';
import {dlsAlt, dlsColor, dlsZIndex} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import FilterSearch from './FilterSearch';

export interface FilterHeaderProps {
  clearSearch?: boolean;
  onSearchValueChange: (value: string) => void;
  onToggleSelectAll: Function;
  setClearSearch?: Function;
  inputLength: number
}

const useStyles = makeStyles((theme: DLSTheme) => {
  const {
    dls: {
      motion: {
        element: {
          largeExpansion: {
            close: expansionClose,
            open: expansionOpen
          }
        }
      }
    }
  } = theme;
  return {
    headerSearch : {
      display : 'block'
    },
    headerOptions : {
      display        : 'inline-flex',
      alignItems     : 'center',
      justifyContent : 'flex-end'
    },
    filterOptionsWrapper : {
      padding         : '8px 4px',
      width           : (filters: any[]) => `${100 / (filters.length + 1)}%`,
      backgroundColor : 'transparent !important'
    },
    filterSelectWrapperHidden : {
      maxWidth       : '0px',
      transition     : `max-width 500ms ease-out, opacity ${expansionClose}`,
      '& .selectAll' : {
        opacity : '0'
      },
      '& .clearAll' : {
        opacity : '0'
      }
    },
    filterSelectWrapper : {
      maxWidth   : '300px',
      transition : `max-width ${expansionOpen}, opacity ${expansionClose}`,
      '& span'   : {
        ...dlsAlt('typography', 'cta2'),
        cursor : 'pointer'
      },
      '& .selectAll' : {
        color   : dlsColor('aaBlue'),
        margin  : '0 25px',
        opacity : '1'
      },
      '& .clearAll' : {
        color   : dlsColor('grey2'),
        opacity : '1'
      }
    },
    filterSelectWrapperMobile : {
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'space-evenly',
      '& span'       : {
        ...dlsAlt('typography', 'cta2'),
        cursor : 'pointer'
      },
      '& .selectAll' : {
        color : dlsColor('aaBlue')
      },
      '& .clearAll' : {
        color : dlsColor('grey2')
      }
    },
    inputOverlay : {
      position       : 'absolute',
      right          : '100px',
      color          : dlsColor('grey4'),
      fontSize       : '.8rem',
      height         : '40px',
      zIndex         : dlsZIndex('globalGroupChooserSearchInputOverlay'),
      display        : 'flex',
      justifyContent : 'flex-end',
      alignItems     : 'center'
    }
  };
});

/**
 * The FilterHeader component provides search functionality of filter
 * option data, and uses the callback function from the onToggleSelectAll
 * prop to provide select all, and clear all functionality.
 * 
 * ```typescript
 * 
 * import FilterHeader from './FilterHeader';
 * 
 * <FilterHeader clearSearch={clearSearch}
 *   onSearchValueChange={handleSearchValueChange}
 *   onToggleSelectAll={handleToggleSelectAll} />
 * 
 * ```
 */

export const FilterHeader = ({
  clearSearch,
  onSearchValueChange,
  onToggleSelectAll,
  setClearSearch,
  inputLength = 3
}: FilterHeaderProps) => {
  const [openFilterSearch, setOpenFilterSearch] = useState(false);
  const [showOverlay, setShowOverlay]   = useState('');
  const [valueLength, setValueLength]   = useState(0);
  const classes = useStyles();

  function handleSelectAll(selected: boolean) {
    onToggleSelectAll(selected);
  }

  function handleToggleFilterSearch() {
    if (openFilterSearch) {
      onSearchValueChange('');
    }
    setOpenFilterSearch(!openFilterSearch);
    setClearSearch?.(false);
    setShowOverlay('');
  }

  const debounceFieldChange = debounce((value) => {
    const valLength          = value.length;
    const shortInput         = value.length < inputLength;
    const shortExistingValue = valueLength < inputLength;
    const shortInvalid       = valLength && shortInput && shortExistingValue;
    const shortChange        = shortInput && !shortExistingValue;
    if (shortInvalid || shortChange) {
      setShowOverlay(translate('common:search.keepTyping'));
      if (shortChange) {
        onSearchValueChange('');
      }
    } else if (!valLength || !shortInput) {
      setShowOverlay('');
      onSearchValueChange(!shortInput ? value : '');
    }
    setValueLength(valLength);
  }, 400);

  function handleSearchValueChange(value: string) {
    debounceFieldChange(value);
  }

  return (
    <>
      {showOverlay && (
        <span className={classes.inputOverlay}>
          {showOverlay}
        </span>
      )}
      <Hidden show='desktop'>
        <div className={classes.headerOptions}>
          <FilterSearch fullWidth
            clearSearch={clearSearch}
            onChange={handleSearchValueChange}
            openFilterSearch={openFilterSearch}
            setOpenFilterSearch={handleToggleFilterSearch} />
          <div className={
            openFilterSearch
              ? classes.filterSelectWrapperHidden
              : classes.filterSelectWrapper
          }>
            <span className='selectAll'
              onClick={function() { handleSelectAll(true); }}>
              {translate('common:columnChooser.select')}
            </span>
            <span className='clearAll'
              onClick={function() { handleSelectAll(false); }}>
              {translate('common:columnChooser.clear')}
            </span>
          </div>
        </div>
      </Hidden>
      <Hidden show='mobile'>
        <FilterSearch fullWidth
          clearSearch={clearSearch}
          onChange={handleSearchValueChange} />
        <div className={classes.filterSelectWrapperMobile}>
          <span className='selectAll'
            onClick={function() { handleSelectAll(true); }}>
            {translate('common:columnChooser.select')}
          </span>
          <span className='clearAll'
            onClick={function() { handleSelectAll(false); }}>
            {translate('common:columnChooser.clear')}
          </span>
        </div>
      </Hidden>
    </>
  );
};

export default FilterHeader;
